import React, { ReactNode } from 'react';
import { Switch, FormControlLabel } from '@mui/material';
import { buildClassName } from 'utils/build-class-name';

//#region Types
export type ToggleSwitchSize = 'small' | 'medium';
export type ToggleSwitchLabelPlacement = 'start' | 'end';

export interface ToggleSwitchProps {
  /** Whether the switch is on (controlled) */
  checked: boolean;
  /** Handler invoked with the new checked state when toggled */
  onChange: (checked: boolean) => void;
  /** Optional label rendered next to the switch */
  label?: string | ReactNode;
  /** Where the label sits relative to the switch */
  labelPlacement?: ToggleSwitchLabelPlacement;
  /** Size variant */
  size?: ToggleSwitchSize;
  /** Disable the switch */
  disabled?: boolean;
  /** Aria label describing the switch when no visible label is provided */
  ariaLabel?: string;
  /** Additional CSS class applied to the root element */
  className?: string;
  /** Test ID for automated testing */
  testId?: string;
}
//#endregion

/**
 * ToggleSwitch - A binary on/off switch.
 *
 * Atomic Level: Molecule
 *
 * A single control that can be toggled either on or off. Clicking it flips the
 * state and calls `onChange` with the new checked value.
 *
 * Features:
 * - Controlled on/off state
 * - Optional label with configurable placement (start or end)
 * - Multiple sizes: small, medium
 * - Disabled state
 * - Accessible: `role="switch"` with `aria-checked` (provided by MUI Switch)
 * - Built on Material UI Switch for consistent styling
 *
 * @example
 * ```tsx
 * <ToggleSwitch
 *   checked={alertsEnabled}
 *   onChange={setAlertsEnabled}
 *   label="Email alerts"
 * />
 * ```
 */
export default function ToggleSwitch({
  checked,
  onChange,
  label,
  labelPlacement = 'end',
  size = 'medium',
  disabled = false,
  ariaLabel,
  className,
  testId,
}: ToggleSwitchProps) {
  const muiSize = size === 'small' ? 'small' : 'medium';

  const switchElement = (
    <Switch
      checked={checked}
      onChange={(_event, isChecked) => onChange(isChecked)}
      size={muiSize}
      disabled={disabled}
      inputProps={{ 'aria-label': ariaLabel }}
      data-testid={testId}
      disableRipple
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: 'var(--themes-colors-primary-font-color, white)',
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          backgroundColor: 'var(--themes-colors-primary-filters, black)',
          opacity: 1,
        },
        '& .MuiSwitch-switchBase.Mui-focusVisible .MuiSwitch-thumb': {
          outline: '2px solid var(--themes-colors-primary, black)',
          outlineOffset: '2px',
        },
        '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      }}
    />
  );

  if (label === undefined) {
    return <span className={buildClassName(className)}>{switchElement}</span>;
  }

  return (
    <FormControlLabel
      className={buildClassName(className)}
      control={switchElement}
      label={label}
      labelPlacement={labelPlacement}
      disabled={disabled}
      sx={{
        margin: 0,
        gap: '8px',
        justifyContent: labelPlacement === 'start' ? 'space-between' : 'flex-start',
        '& .MuiFormControlLabel-label': {
          fontFamily: 'Manrope, sans-serif',
          fontSize: size === 'small' ? '13px' : '14px',
          fontWeight: 500,
          color: 'var(--themes-colors-text, rgba(0, 0, 0, .87))',
        },
      }}
    />
  );
}
